import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { boardFetch, collection, db, isPreviewStill, onSnapshot } from "../boardData";
import { useCoordinates } from "./common";

// A scrolling ticker band in the look of the legacy board's announcements
// ticker: a label chip with a pulsing dot, text gliding right to left behind
// soft edge fades. What it scrolls (config.source):
//   "text"     the widget's own messages (config.messages)
//   "memos"    the newest memos from the agency memo feed
//   "legacy"   the Legacy Single Board's announcements text
//   "alerts"   active NWS alerts for the board's location
// config: label, speed ("slow" | "normal" | "fast"), style ("blue" | "red" |
// "amber" | "dark"), limit (memos), emptyText.

const SPEED_PX_PER_SEC = { slow: 60, normal: 110, fast: 180 };
const ALLOWED_PRIVACY = ["All", "stationboard", "Stationboard"];

function useMemoItems(agency, enabled, limit) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!enabled || !agency) return undefined;
    return onSnapshot(
      collection(db, agency),
      (snap) => {
        setItems(
          snap.docs
            .map((d) => d.data())
            .filter((m) => !m.privacy || ALLOWED_PRIVACY.includes(m.privacy))
            .sort((a, b) => (b.postTime?.seconds || 0) - (a.postTime?.seconds || 0))
            .slice(0, limit)
            .map((m) => m.post || [m.title, m.message || m.text || m.body].filter(Boolean).join(": "))
            .filter(Boolean)
        );
      },
      () => setItems([])
    );
  }, [agency, enabled, limit]);
  return items;
}

function useLegacyAnnouncement(agency, enabled) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!enabled || !agency) return undefined;
    return onSnapshot(
      collection(db, `${agency} Station Board`),
      (snap) => {
        const d = snap.docs.map((x) => x.data()).find((x) => typeof x.announcementsText === "string");
        setText(d?.announcementsText?.trim() || "");
      },
      () => setText("")
    );
  }, [agency, enabled]);
  return text ? [text] : [];
}

function useAlertItems(lat, lng, enabled) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!enabled || lat == null || lng == null) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await boardFetch(`https://api.weather.gov/alerts/active?point=${lat},${lng}`, {
          headers: { Accept: "application/geo+json" },
        });
        const json = await res.json();
        if (cancelled) return;
        setItems(
          (json.features || [])
            .map((f) => f.properties || {})
            .map((p) => [p.event, p.headline || p.areaDesc].filter(Boolean).join(" — "))
            .filter(Boolean)
        );
      } catch (err) {
        console.warn("ticker alerts failed", err);
      }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [lat, lng, enabled]);
  return items;
}

const DEFAULT_LABELS = { text: "Announcements", memos: "Latest Memos", legacy: "Announcements", alerts: "Weather Alerts" };
const DEFAULT_EMPTY = { alerts: "No active weather alerts" };

const TickerWidget = ({ config = {}, agency, board }) => {
  const source = config.source || "text";
  const { lat, lng } = useCoordinates(agency, board);
  const memoItems = useMemoItems(agency, source === "memos", config.limit || 5);
  const legacyItems = useLegacyAnnouncement(agency, source === "legacy");
  const alertItems = useAlertItems(lat, lng, source === "alerts");

  const ownItems = (config.messages || []).map((m) => String(m || "").trim()).filter(Boolean);
  const items = { text: ownItems, memos: memoItems, legacy: legacyItems, alerts: alertItems }[source] || [];
  const emptyText = config.emptyText ?? DEFAULT_EMPTY[source] ?? "";
  const shown = items.length ? items : emptyText ? [emptyText] : [];
  const label = config.label ?? DEFAULT_LABELS[source] ?? "";

  // Scroll at a steady speed whatever the text length: time one loop (half the
  // doubled track) by its measured width.
  const trackRef = useRef(null);
  const [duration, setDuration] = useState(25);
  const content = shown.join(" • ");
  const pxPerSec = SPEED_PX_PER_SEC[config.speed] || SPEED_PX_PER_SEC.normal;
  useLayoutEffect(() => {
    const w = trackRef.current ? trackRef.current.scrollWidth / 2 : 0;
    if (w > 0) setDuration(Math.max(8, w / pxPerSec));
  }, [content, pxPerSec]);

  const still = isPreviewStill();
  return (
    <div className={`sb-ticker sb-ticker-${config.style || "blue"}`}>
      {label && (
        <div className="sb-ticker-label">
          <span className="sb-ticker-dot" aria-hidden="true" />
          {label}
        </div>
      )}
      <div className="sb-ticker-viewport">
        {shown.length > 0 && (
          <div
            className="sb-ticker-track"
            ref={trackRef}
            style={{ animationDuration: `${duration}s`, animationPlayState: still ? "paused" : "running" }}
          >
            <span className="sb-ticker-item">{content}</span>
            <span className="sb-ticker-item" aria-hidden="true">{content}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TickerWidget;
